import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { configAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Loader2, AlertCircle, Lock, Mail, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import siteConfig from '../config/siteConfig';

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, isAuthenticated, error: authError } = useAuth();
  const { company, logo, login: loginText } = siteConfig;
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [clinicName, setClinicName] = useState(company.name);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/app/dashboard');
    }
    configAPI.getClinic().then(res => {
      setClinicName(res.data.name);
    }).catch(() => {});
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setLoading(true);
    const result = await login(email, password);
    
    if (result.success) {
      toast.success('Welcome back!');
      navigate('/app/dashboard');
    } else {
      toast.error(result.error);
    }
    setLoading(false);
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 50%, #e2e8f0 100%)'
      }}
      data-testid="login-page"
    >
      {/* Background effects */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-primary/3 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-r from-primary/5 to-transparent rounded-full blur-3xl" />
      </div>

      {/* Grid pattern overlay */}
      <div 
        className="absolute inset-0 opacity-[0.02]"
        style={{
          backgroundImage: `linear-gradient(rgba(0,0,0,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.1) 1px, transparent 1px)`,
          backgroundSize: '50px 50px'
        }}
      />

      <div className="w-full max-w-md space-y-8 animate-fadeIn relative z-10">
        {/* Logo & Branding */}
        <div className="text-center">
          <div className="relative inline-block">
            <div className="absolute inset-0 bg-primary/10 rounded-3xl blur-xl" />
            <img 
              src={logo.url} 
              alt={logo.alt} 
              className="w-24 h-24 mx-auto rounded-2xl object-contain relative z-10 border border-slate-200 bg-white shadow-lg" 
            />
          </div>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900 mt-6">{clinicName}</h1>
          <p className="text-slate-500 mt-2 flex items-center justify-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            {loginText.subtitle}
          </p>
        </div>

        {/* Login Card */}
        <div className="premium-card p-8 animate-fadeIn" style={{ animationDelay: '0.1s' }}>
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-slate-900">{loginText.signInTitle}</h2>
            <p className="text-sm text-slate-500 mt-1">{loginText.signInSubtitle}</p>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-5">
            {authError && (
              <Alert className="bg-red-50 border-red-200 text-red-700">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{authError}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-600 text-sm">Email Address</Label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                <Input 
                  id="email" 
                  type="email" 
                  placeholder="you@clinic.com" 
                  value={email} 
                  onChange={(e) => setEmail(e.target.value)} 
                  required 
                  className="h-12 pl-12 rounded-xl bg-white border-slate-200 text-slate-900 placeholder:text-slate-400 focus:border-primary/50 focus:ring-2 focus:ring-primary/10 transition-all duration-300"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-600 text-sm">Password</Label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                <Input 
                  id="password" 
                  type="password" 
                  placeholder="••••••••" 
                  value={password} 
                  onChange={(e) => setPassword(e.target.value)} 
                  required 
                  className="h-12 pl-12 rounded-xl bg-white border-slate-200 text-slate-900 placeholder:text-slate-400 focus:border-primary/50 focus:ring-2 focus:ring-primary/10 transition-all duration-300"
                />
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full h-12 rounded-xl bg-primary hover:bg-primary/90 text-white font-semibold btn-primary-glow transition-all duration-300"
              disabled={loading}
              data-testid="login-btn"
            >
              {loading && <Loader2 className="mr-2 h-5 w-5 animate-spin" />}
              {loginText.signInButton}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-slate-500">{loginText.noAccountText}</p>
        </div>

        {/* Footer */}
        <div className="text-center">
          <p className="text-xs text-slate-400">
            Secure access • HIPAA Compliant • © 2026 Apex Medical Group
          </p>
        </div>
      </div>
    </div>
  );
}
